import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const QuoteCtx = createContext(null);
const STORAGE_KEY = "spectrum_quote_v2";

const load = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v === null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
};

const daysBetween = (start, end) => {
  if (!start || !end) return 1;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 1;
  return Math.round((e - s) / 86400000) + 1;
};

const pickDiscountPercent = (days, tiers) => {
  const applicable = tiers.filter((t) => days >= t.min_days);
  if (!applicable.length) return 0;
  return applicable.reduce((a, b) => (b.min_days > a.min_days ? b : a)).percent;
};

export const QuoteProvider = ({ children }) => {
  const [lines, setLines] = useState(() => load(STORAGE_KEY + "_lines", []));
  const [startDate, setStartDate] = useState(() =>
    load(STORAGE_KEY + "_start", ""),
  );
  const [endDate, setEndDate] = useState(() =>
    load(STORAGE_KEY + "_end", ""),
  );
  const [availability, setAvailability] = useState({});
  const [avLoading, setAvLoading] = useState(false);
  const [discountTiers, setDiscountTiers] = useState([]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY + "_lines", JSON.stringify(lines));
  }, [lines]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY + "_start", JSON.stringify(startDate));
  }, [startDate]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY + "_end", JSON.stringify(endDate));
  }, [endDate]);

  useEffect(() => {
    api
      .get("/discount-tiers")
      .then((r) => setDiscountTiers(r.data.tiers || []))
      .catch(() => {});
  }, []);

  const refreshAvailability = useCallback(async () => {
    if (!startDate || !endDate || new Date(endDate) < new Date(startDate)) {
      setAvailability({});
      return;
    }
    setAvLoading(true);
    try {
      const { data } = await api.post("/items/availability", {
        start_date: startDate,
        end_date: endDate,
      });
      const map = {};
      for (const it of data.items) map[it.item_id] = it;
      setAvailability(map);
    } catch {
      setAvailability({});
    } finally {
      setAvLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    refreshAvailability();
  }, [refreshAvailability]);

  const days = daysBetween(startDate, endDate);

  const addItem = (item) => {
    const info = availability[item.id];
    const cap = info != null ? info.available_qty : item.quantity;
    setLines((prev) => {
      const existing = prev.find((l) => l.item_id === item.id);
      if (existing) {
        if (existing.quantity >= cap) return prev;
        return prev.map((l) =>
          l.item_id === item.id ? { ...l, quantity: l.quantity + 1 } : l,
        );
      }
      if (cap < 1) return prev;
      return [
        ...prev,
        {
          item_id: item.id,
          name: item.name,
          daily_price: item.daily_price,
          quantity: 1,
          max_qty: item.quantity,
        },
      ];
    });
  };

  const removeItem = (itemId) =>
    setLines((prev) => prev.filter((l) => l.item_id !== itemId));

  const setQty = (itemId, qty) =>
    setLines((prev) =>
      prev
        .map((l) => {
          if (l.item_id !== itemId) return l;
          const info = availability[itemId];
          const cap = info != null ? info.available_qty : l.max_qty || 99;
          return { ...l, quantity: Math.max(1, Math.min(qty, cap)) };
        })
        .filter((l) => l.quantity > 0),
    );

  const clear = () => setLines([]);

  const totalItems = lines.reduce((s, l) => s + l.quantity, 0);
  const subtotal = lines.reduce((s, l) => s + l.daily_price * l.quantity, 0);
  const subtotalBeforeDiscount = subtotal * days;
  const discountPercent = pickDiscountPercent(days, discountTiers);
  const discountAmount =
    Math.round(subtotalBeforeDiscount * discountPercent) / 100;
  const total = Math.round((subtotalBeforeDiscount - discountAmount) * 100) / 100;

  return (
    <QuoteCtx.Provider
      value={{
        lines,
        addItem,
        removeItem,
        setQty,
        clear,
        totalItems,
        subtotal,
        subtotalBeforeDiscount,
        discountPercent,
        discountAmount,
        total,
        days,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        availability,
        avLoading,
        refreshAvailability,
        discountTiers,
        setDiscountTiers,
      }}
    >
      {children}
    </QuoteCtx.Provider>
  );
};

export const useQuote = () => useContext(QuoteCtx);
